import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  Download, 
  Mail,
  Loader2,
  AlertCircle,
  Home,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { getPaymentStatus, getPolicyBySession } from "@/lib/api";
import confetti from "@/lib/confetti";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [quote, setQuote] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [checkingAgain, setCheckingAgain] = useState(false);

  const handleSendConfirmationEmail = async () => {
    if (!policy?.policy_number) {
      toast.error('Policy information not available yet');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/policy/${policy.policy_number}/send-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setEmailSent(true);
        toast.success('Confirmation email sent successfully!');
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to send confirmation email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    const hasDecimals = num % 1 !== 0;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    const pollStatus = async () => {
      try {
        console.log(`[SuccessPage] Polling payment status for session: ${sessionId}`);
        const status = await getPaymentStatus(sessionId);
        console.log(`[SuccessPage] Payment status response:`, status);
        setPaymentStatus(status);

        if (status.payment_status === "paid") {
          console.log(`[SuccessPage] Payment confirmed as paid, checking policy status...`);
          // Payment successful - check issuance status
          const issuanceResponse = await fetch(`${API_URL}/api/policy/status/${sessionId}`);
          const issuanceData = await issuanceResponse.json();
          console.log(`[SuccessPage] Policy issuance status:`, issuanceData);
          
          // Check status to determine next step
          if (issuanceData.status === 'pending_info') {
            // Redirect to policyholder info collection
            console.log(`[SuccessPage] Redirecting to policyholder-info page`);
            navigate(`/policyholder-info?session_id=${sessionId}`);
            return;
          } else if (issuanceData.status === 'issued' && issuanceData.policy_number) {
            // Policy already issued - redirect to confirmation
            console.log(`[SuccessPage] Policy issued, redirecting to confirmation`);
            navigate(`/confirmation?session_id=${sessionId}`);
            return;
          }
          
          // Fallback - get policy details
          console.log(`[SuccessPage] Fetching policy details...`);
          const policyData = await getPolicyBySession(sessionId);
          setPolicy(policyData.policy);
          setQuote(policyData.quote);
          setLoading(false);
          
          // Trigger confetti
          confetti();
          
          toast.success("Payment successful! Your coverage is now active.");
        } else if (status.status === "expired") {
          console.log(`[SuccessPage] Payment session expired`);
          setLoading(false);
          toast.error("Payment session expired. Please try again.");
        } else if (pollCount < 20) {
          // Continue polling (3-second intervals, up to 60 seconds)
          console.log(`[SuccessPage] Payment not yet confirmed, polling again (${pollCount + 1}/20)...`);
          setTimeout(() => setPollCount(prev => prev + 1), 3000);
        } else {
          console.log(`[SuccessPage] Polling timeout reached, showing pending state`);
          setLoading(false);
          toast.info("Payment is still processing. You can check again or contact support.");
        }
      } catch (error) {
        console.error("[SuccessPage] Error checking payment status:", error);
        if (pollCount < 8) {
          setTimeout(() => setPollCount(prev => prev + 1), 3000);
        } else {
          setLoading(false);
          toast.error("Unable to verify payment status. Please contact support.");
        }
      }
    };

    pollStatus();
  }, [sessionId, pollCount, navigate]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            No Session Found
          </h1>
          <p className="text-slate-700 mb-6">
            It looks like you arrived here directly. Please complete a quote first.
          </p>
          <Button onClick={() => navigate('/quote')} className="bg-blue-600 hover:bg-blue-700">
            Get a Quote
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Processing Your Payment
          </h1>
          <p className="text-slate-700">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  // Manual re-check function
  const checkPaymentAgain = async () => {
    setCheckingAgain(true);
    try {
      const status = await getPaymentStatus(sessionId);
      setPaymentStatus(status);
      
      if (status.payment_status === "paid") {
        toast.success("Payment confirmed!");
        // Re-trigger the full flow
        setLoading(true);
        setPollCount(0);
      } else {
        toast.info("Payment is still processing. Please wait a moment and try again.");
      }
    } catch (error) {
      toast.error("Unable to check payment status. Please try again.");
    } finally {
      setCheckingAgain(false);
    }
  };

  if (paymentStatus?.payment_status !== "paid") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payment {paymentStatus?.status === "expired" ? "Expired" : "Pending"}
          </h1>
          <p className="text-slate-700 mb-6">
            {paymentStatus?.status === "expired" 
              ? "Your payment session has expired. Please start a new quote."
              : "Your payment is still being confirmed. This may take a moment."}
          </p>
          <div className="flex flex-col gap-3">
            {paymentStatus?.status !== "expired" && (
              <Button 
                onClick={checkPaymentAgain} 
                disabled={checkingAgain}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="check-payment-again-btn"
              >
                {checkingAgain ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Checking...
                  </>
                ) : (
                  'Check Payment Status'
                )}
              </Button>
            )}
            <Button onClick={() => navigate('/quote')} variant={paymentStatus?.status === "expired" ? "default" : "outline"} className={paymentStatus?.status === "expired" ? "bg-blue-600 hover:bg-blue-700" : ""}>
              Get New Quote
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a 
            href="https://losspay.com"
            className="flex items-center transition-opacity hover:opacity-80"
          >
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-[28px] sm:h-[36px] w-auto"
            />
          </a>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Message */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            You're Protected!
          </h1>
          <p className="text-lg text-slate-700">
            Your diminished value coverage is now active.
          </p>
        </div>

        {/* Policy Details Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden animate-slide-up">
          <div className="p-6 bg-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-200">Policy Number</p>
                <p className="text-2xl font-bold font-mono" data-testid="policy-number">
                  {policy?.policy_number || "Processing..."}
                </p>
              </div>
              <Shield className="h-12 w-12 text-blue-200" />
            </div>
          </div>

          {quote && (
            <div className="p-6">
              {/* Vehicle Info */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  Covered Vehicle
                </h3>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {quote.vehicle_info?.year} {quote.vehicle_info?.make} {quote.vehicle_info?.model}{quote.vehicle_info?.trim ? ` ${quote.vehicle_info.trim}` : ''}
                </p>
                <p className="text-sm text-slate-700 font-mono">{quote.vin}</p>
                {quote.vehicle_info?.estimated_value > 0 && (
                  <p className="text-sm text-slate-700 mt-1">Value: {formatCurrency(quote.vehicle_info.estimated_value)}</p>
                )}
              </div>

              {/* Coverage Details */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  Coverage Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-700">Coverage Level</p>
                    <p className="font-semibold text-slate-900">{quote.coverage_level_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-700">Coverage Amount</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(quote.coverage_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-700">Deductible</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(quote.deductible_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-700">Payment</p>
                    <p className="font-semibold text-slate-900 capitalize">{quote.payment_frequency?.replace('_', '-')}</p>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  Payment Summary
                </h3>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-slate-700">Amount Paid</p>
                      <p className="text-xs text-slate-700">
                        {quote.installment_count > 1 
                          ? `First of ${quote.installment_count} payments`
                          : "Annual payment"}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {formatCurrency(paymentStatus?.amount_total / 100 || quote.installment_amount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <Button
                  data-testid="download-policy-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => toast.info("Policy document download coming soon!")}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Policy Document
                </Button>
                <Button
                  data-testid="email-confirmation-btn"
                  variant="outline"
                  className="w-full"
                  onClick={handleSendConfirmationEmail}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : emailSent ? (
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  {sendingEmail ? 'Sending...' : emailSent ? 'Email Sent!' : 'Email Confirmation'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="mt-8 bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            What's Next?
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Check your email</p>
                <p className="text-sm text-slate-700">We've sent your policy details and welcome kit.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Keep your documents safe</p>
                <p className="text-sm text-slate-700">Store your policy number and documents in a safe place.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Contact us if needed</p>
                <p className="text-sm text-slate-700">Call 833-LOSSPAY or email info@losspay.com for any questions.</p>
              </div>
            </li>
          </ul>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-700 hover:text-slate-900"
          >
            <Home className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
